import { Link } from 'react-router-dom';

const Hero = ({ content }) => {
  // Ensure content is an object before destructuring and provide fallbacks
  const safeContent = typeof content === 'object' && content !== null ? content : {};
  
  // Use default values as fallback if content is not provided
  const {
    title = "Your links,",
    subtitle = "beautifully organized",
    description = "Create a stunning link-in-bio page in minutes. Share all your important links from one beautiful, customizable page.",
    ctaText = "Get Started Free",
    ctaLink = "/signup",
    secondaryCtaText = "View Demo",
    secondaryCtaLink = "#demo",
    showCreatorName = true,
    creatorName = "Sarah Johnson",
    creatorTitle = "Digital Creator & Designer",
    demoLinks = [
      "My Portfolio",
      "Latest Blog Post",
      "YouTube Channel",
      "Book a Call",
      "Newsletter"
    ]
  } = safeContent;
  return (
    <section className="relative overflow-hidden bg-gradient-to-br from-purple-50 via-white to-pink-50 pt-16 pb-20 sm:pt-24 sm:pb-32">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-purple-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse-slow"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-pink-200 rounded-full mix-blend-multiply filter blur-xl opacity-30 animate-pulse-slow" style={{ animationDelay: '2s' }}></div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="lg:grid lg:grid-cols-12 lg:gap-8">
          <div className="sm:text-center md:max-w-2xl md:mx-auto lg:col-span-6 lg:text-left lg:flex lg:items-center">
            <div className="animate-fade-in-up">
              {/* Heading */}
              <h1 className="text-4xl font-bold text-gray-900 tracking-tight sm:text-5xl md:text-6xl">
                <span className="block">{title}</span>
                <span className="block bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  {subtitle}
                </span>
              </h1>
              
              {/* Description */}
              <p className="mt-3 text-base text-gray-500 sm:mt-5 sm:text-xl lg:text-lg xl:text-xl">
                {description}
              </p>
              
              {/* CTA Buttons */}
              <div className="mt-8 sm:max-w-lg sm:mx-auto sm:text-center lg:text-left lg:mx-0">
                <div className="flex flex-col sm:flex-row gap-4">
                  <Link
                    to={ctaLink}
                    className="inline-flex items-center justify-center px-6 py-3 border border-transparent text-base font-medium rounded-lg text-white bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 transition-all duration-200 transform hover:scale-105 hover:shadow-lg animate-bounce-in"
                  >
                    {ctaText}
                  </Link>
                  <Link
                    to={secondaryCtaLink}
                    className="inline-flex items-center justify-center px-6 py-3 border border-gray-300 text-base font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 transition-all duration-200 transform hover:scale-105 animate-bounce-in"
                    style={{ animationDelay: '0.1s' }}
                  >
                    {secondaryCtaText}
                  </Link>
                </div>
                <p className="mt-3 text-sm text-gray-500 animate-fade-in" style={{ animationDelay: '0.3s' }}>
                  No credit card required • Free forever plan available
                </p>
              </div>
            </div>
          </div>
          
          {/* Phone Mockup */}
          <div className="mt-12 relative sm:max-w-lg sm:mx-auto lg:mt-0 lg:max-w-none lg:mx-0 lg:col-span-6 lg:flex lg:items-center">
            <div className="relative mx-auto w-full max-w-[300px] lg:max-w-[360px] animate-fade-in-up" style={{ animationDelay: '0.2s' }}>
              {/* Phone Frame */}
              <div className="relative bg-gray-900 rounded-[3rem] p-3 shadow-2xl transform hover:scale-105 transition-transform duration-300">
                <div className="bg-gray-100 rounded-[2.5rem] p-2 relative overflow-hidden">
                  {/* Notch */}
                  <div className="absolute top-0 inset-x-0 flex justify-center">
                    <div className="bg-gray-900 h-6 w-32 rounded-b-2xl"></div>
                  </div>
                  
                  {/* Screen Content */}
                  <div className="bg-white rounded-[2rem] px-4 py-8 h-[600px] overflow-y-auto custom-scrollbar">
                    {/* Profile Section */}
                    <div className="text-center mb-6 animate-fade-in-down" style={{ animationDelay: '0.4s' }}>
                      <div className="w-20 h-20 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full mx-auto mb-3 animate-bounce-in" style={{ animationDelay: '0.5s' }}></div>
                      <h3 className="font-semibold text-gray-900">{creatorName}</h3>
                      <p className="text-sm text-gray-600">{creatorTitle}</p>
                    </div>
                    
                    {/* Social Links */}
                    <div className="flex justify-center gap-4 mb-6">
                      {['twitter', 'instagram', 'linkedin', 'youtube'].map((social, index) => (
                        <div 
                          key={social}
                          className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center transform hover:scale-110 transition-transform animate-scale-in"
                          style={{ animationDelay: `${0.6 + index * 0.1}s` }}
                        >
                          <div className="w-5 h-5 bg-gray-400 rounded"></div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Links */}
                    <div className="space-y-3">
                      {demoLinks.map((link, index) => (
                        <div 
                          key={`demo-link-${index}`}
                          className="block w-full p-4 bg-gray-50 rounded-xl text-center text-gray-700 font-medium transform hover:scale-105 transition-all duration-200 hover:bg-purple-50 animate-fade-in-up"
                          style={{ animationDelay: `${0.8 + index * 0.1}s` }}
                        >
                          {link}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Floating badges */}
              <div className="absolute -top-4 -right-4 bg-white rounded-full px-3 py-1 shadow-lg text-sm font-medium text-purple-600 animate-bounce-in" style={{ animationDelay: '1s' }}>
                ✨ Live Preview
              </div>
              <div className="absolute -bottom-4 -left-4 bg-white rounded-full px-3 py-1 shadow-lg text-sm font-medium text-pink-600 animate-bounce-in" style={{ animationDelay: '1.2s' }}>
                🚀 Easy Setup
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Custom scrollbar styles */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 4px;
        }
        .custom-scrollbar::-webkit-scrollbar-track {
          background: transparent;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: #e5e7eb;
          border-radius: 2px;
        }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #d1d5db;
        }
      `}</style>
    </section>
  );
};

export default Hero; 